"use client"

import { useEffect, useState, useRef } from "react"
import Link from "next/link"

/* --- 스크롤 애니메이션 훅 --- */
function useInView() {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.unobserve(el)
        }
      },
      { threshold: 0.15 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return { ref, visible }
}

function FadeIn({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const { ref, visible } = useInView()
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

/* --- 폰 목업 래퍼 --- */
function PhoneMockup({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-[260px] md:w-[300px]">
      <div className="rounded-[2rem] border-[8px] border-gray-800 bg-gray-800 shadow-2xl">
        <div className="mx-auto mt-1 h-5 w-24 rounded-full bg-gray-900" />
        <div className="mt-1 overflow-hidden rounded-[1.5rem] bg-white">
          <div className="h-[480px] md:h-[500px] overflow-hidden">
            {children}
          </div>
        </div>
        <div className="mx-auto my-2 h-1 w-20 rounded-full bg-gray-600" />
      </div>
    </div>
  )
}

/* --- 자동 슬라이드 설문 스크린 --- */
function SurveyAutoSlide() {
  const [currentQ, setCurrentQ] = useState(0)

  const questions = [
    {
      question: "나트랑에서 꼭 해보고 싶은 활동은? \uD83C\uDFD6\uFE0F",
      progress: "3/12",
      percent: 25,
      options: [
        { text: "\uD83C\uDFC4 수상 스포츠", selected: true },
        { text: "\uD83E\uDDD6 스파 & 머드바스", selected: false },
        { text: "\uD83C\uDFDB\uFE0F 포나가르 사원", selected: false },
        { text: "\uD83C\uDFA2 빈펄랜드", selected: false },
      ]
    },
    {
      question: "여행 중 식사 스타일은? \uD83C\uDF5C",
      progress: "5/12",
      percent: 42,
      options: [
        { text: "\uD83C\uDF7D\uFE0F 현지 로컬 맛집", selected: false },
        { text: "\uD83E\uDD58 호텔 뷔페", selected: true },
        { text: "\uD83C\uDF55 편하게 패스트푸드", selected: false },
        { text: "\uD83E\uDD57 건강식 위주", selected: false },
      ]
    },
    {
      question: "숙소에서 가장 중요한 건? \uD83C\uDFE8",
      progress: "7/12",
      percent: 58,
      options: [
        { text: "\uD83D\uDCCD 위치 (관광지 근처)", selected: false },
        { text: "\uD83D\uDCB0 가성비", selected: false },
        { text: "\uD83C\uDFCA 수영장/부대시설", selected: true },
        { text: "\u2728 청결도", selected: false },
      ]
    },
    {
      question: "하루 일정 강도는? \u26A1",
      progress: "9/12",
      percent: 75,
      options: [
        { text: "\uD83D\uDE34 느긋하게 (2~3곳)", selected: true },
        { text: "\uD83D\uDEB6 적당히 (3~4곳)", selected: false },
        { text: "\uD83C\uDFC3 활동적 (5곳+)", selected: false },
        { text: "\uD83E\uDD37 상관없음", selected: false },
      ]
    }
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentQ(prev => (prev + 1) % questions.length)
    }, 3000)
    return () => clearInterval(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const q = questions[currentQ]

  return (
    <div className="p-4 bg-[#FFF8F0] h-full">
      <p className="text-[11px] font-semibold text-gray-800">김민수님, 여행 취향을 알려주세요!</p>
      <p className="text-[9px] text-gray-400 mt-0.5">나트랑 가족여행</p>

      {/* 진행률 */}
      <div className="mt-3 flex justify-between text-[9px] text-gray-400">
        <span>질문 {q.progress}</span><span>{q.percent}%</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-gray-200">
        <div className="h-1.5 rounded-full bg-[#FF6B4A] transition-all duration-700" style={{ width: `${q.percent}%` }} />
      </div>

      {/* 질문 */}
      <div className="mt-4 relative overflow-hidden">
        <p key={currentQ} className="text-[13px] font-bold text-gray-800 animate-in fade-in slide-in-from-right-4 duration-500">
          {q.question}
        </p>
      </div>

      {/* 선택지 */}
      <div className="mt-3 space-y-2">
        {q.options.map((opt, j) => (
          <div
            key={`${currentQ}-${j}`}
            className={`rounded-xl border-2 p-2.5 text-[11px] font-medium transition-all duration-300 animate-in fade-in slide-in-from-right-2 ${
              opt.selected
                ? "border-[#FF6B4A] bg-[#FF6B4A]/5 text-[#FF6B4A]"
                : "border-gray-200 text-gray-700"
            }`}
            style={{ animationDelay: `${j * 100}ms` }}
          >
            {opt.text}
          </div>
        ))}
      </div>

      {/* 인디케이터 */}
      <div className="mt-4 flex justify-center gap-1.5">
        {questions.map((_, i) => (
          <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${
            i === currentQ ? "w-4 bg-[#FF6B4A]" : "w-1.5 bg-gray-300"
          }`} />
        ))}
      </div>

      {/* 버튼 */}
      <div className="mt-3 flex gap-2">
        <div className="flex-1 rounded-full border py-2 text-center text-[10px] text-gray-400">&larr; 이전</div>
        <div className="flex-1 rounded-full bg-[#FF6B4A] py-2 text-center text-[10px] text-white font-semibold">다음 &rarr;</div>
      </div>
    </div>
  )
}

/* --- V2 목업 스크린 (스티키 스크롤용, PhoneMockup 래퍼 없이) --- */

function CourseMockupScreenV2() {
  const [phase, setPhase] = useState(0)
  const [insertPos, setInsertPos] = useState(0) // 0: 숙소-맛집 사이, 1: 테마파크-야시장 사이

  // 순환: 위치0 삽입 → 리셋 → 위치1 삽입 → 리셋
  useEffect(() => {
    const timeline = [
      { p: 0, pos: 0, delay: 2000 },  // 기본
      { p: 1, pos: 0, delay: 800 },   // + 활성화 (위치0)
      { p: 2, pos: 0, delay: 1000 },  // 칩 표시
      { p: 3, pos: 0, delay: 1200 },  // 일정 슬라이드인
      { p: 4, pos: 0, delay: 2000 },  // 시간 조정
      { p: 0, pos: 1, delay: 1500 },  // 리셋 → 위치1로
      { p: 1, pos: 1, delay: 800 },   // + 활성화 (위치1)
      { p: 2, pos: 1, delay: 1000 },  // 칩 표시
      { p: 3, pos: 1, delay: 1200 },  // 일정 슬라이드인
      { p: 4, pos: 1, delay: 2000 },  // 시간 조정
    ]
    let step = 0
    let timeout: ReturnType<typeof setTimeout>
    const run = () => {
      const t = timeline[step]
      setPhase(t.p)
      setInsertPos(t.pos)
      step = (step + 1) % timeline.length
      timeout = setTimeout(run, t.delay)
    }
    run()
    return () => clearTimeout(timeout)
  }, [])

  const isActive = (pos: number) => insertPos === pos
  const showChips = (pos: number) => isActive(pos) && phase >= 2
  const showNew = (pos: number) => isActive(pos) && phase >= 3
  const adjusted = phase >= 4

  const PlusButton = ({ pos }: { pos: number }) => (
    <div className="relative flex flex-col items-center py-0.5">
      <div className="absolute left-4 right-4 top-1/2 border-t border-dashed border-gray-300" />
      <div className={`relative z-10 flex items-center justify-center rounded-full bg-white font-bold transition-all duration-500 ${
        isActive(pos) && phase >= 1
          ? "h-6 w-6 border-2 border-[#FF6B4A] text-[#FF6B4A] text-[12px] shadow-md scale-110"
          : "h-4 w-4 border border-dashed border-gray-300 text-gray-300 text-[9px]"
      }`}>+</div>
      {showChips(pos) && (
        <div className="flex gap-1 mt-0.5 transition-all duration-500 animate-in fade-in slide-in-from-bottom-1">
          {pos === 0 ? (
            <>
              <span className={`rounded-full px-1.5 py-0.5 text-[7px] ${phase === 2 && isActive(pos) ? "bg-[#FF6B4A] text-white" : "bg-[#FF6B4A]/10 text-[#FF6B4A]"}`}>🎯 관광</span>
              <span className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[7px] text-orange-500">🍽️ 식당</span>
              <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[7px] text-amber-600">☕ 카페</span>
            </>
          ) : (
            <>
              <span className="rounded-full bg-[#FF6B4A]/10 px-1.5 py-0.5 text-[7px] text-[#FF6B4A]">🎯 관광</span>
              <span className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[7px] text-orange-500">🍽️ 식당</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[7px] ${phase === 2 && isActive(pos) ? "bg-emerald-500 text-white" : "bg-emerald-50 text-emerald-600"}`}>☕ 카페</span>
            </>
          )}
        </div>
      )}
    </div>
  )

  const NewItem = ({ pos, title, emoji, color, time, match }: { pos: number; title: string; emoji: string; color: string; time: string; match: string }) => (
    <div className={`transition-all duration-700 overflow-hidden ${showNew(pos) ? "max-h-16 opacity-100" : "max-h-0 opacity-0"}`}>
      <div className={`rounded-lg border-l-4 bg-opacity-50 p-2 shadow-md ring-1 ${color}`}>
        <div className="flex items-center gap-1">
          <span className="text-[8px] text-gray-400">{adjusted ? time : "✨"}</span>
          <span className="text-[10px]">{emoji}</span>
          <span className="text-[10px] font-semibold">{title}</span>
          <span className="text-[7px] bg-blue-100 text-blue-600 px-1 py-0.5 rounded-full">NEW</span>
        </div>
        <p className="text-[8px] text-gray-500">{match}</p>
      </div>
    </div>
  )

  return (
    <div className="p-3 bg-[#FFF8F0] h-full overflow-hidden">
      <p className="text-[12px] font-bold text-gray-800">AI 추천 결과</p>
      <div className="mt-1.5 flex gap-1">
        <div className="rounded-full bg-[#FF6B4A] px-2.5 py-0.5 text-[8px] font-bold text-white">Day 1</div>
        <div className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[8px] text-gray-500">Day 2</div>
        <div className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[8px] text-gray-500">Day 3</div>
      </div>
      <div className="mt-2 space-y-0">
        {/* 숙소 */}
        <div className="rounded-lg border-l-4 border-l-purple-500 bg-white p-2 shadow-sm">
          <div className="flex items-center gap-1">
            <span className="text-[8px] text-gray-400">09:00</span>
            <span className="text-[10px]">🏨</span>
            <span className="text-[10px] font-semibold text-gray-800">멜리아 빈펄 리조트</span>
          </div>
          <div className="mt-0.5 flex gap-1">
            <span className="rounded-full bg-indigo-50 px-1 py-0.5 text-[7px] text-indigo-600">김민수님 취향</span>
          </div>
        </div>

        {/* + 위치 0: 숙소-맛집 사이 */}
        <PlusButton pos={0} />
        <NewItem pos={0} title="포나가르 사원 관광" emoji="🎯" color="border-l-blue-500 bg-blue-50 ring-blue-200" time="10:30" match="도보 15분 · 매칭률 91%" />

        {/* 맛집 */}
        <div className="rounded-lg border-l-4 border-l-orange-500 bg-white p-2 shadow-sm">
          <div className="flex items-center gap-1">
            <span className="text-[8px] text-gray-400 transition-all duration-500">{adjusted && insertPos === 0 ? "12:30" : "12:00"}</span>
            <span className="text-[10px]">🍽️</span>
            <span className="text-[10px] font-semibold text-gray-800">로컬 씨푸드 레스토랑</span>
          </div>
        </div>

        {/* + 숨김 (맛집-테마파크 사이) */}
        <div className="relative flex items-center justify-center py-0.5">
          <div className="absolute left-4 right-4 border-t border-dashed border-gray-200" />
          <div className="relative z-10 h-3 w-3 rounded-full border border-dashed border-gray-200 bg-white text-[7px] text-gray-300 flex items-center justify-center">+</div>
        </div>

        {/* 테마파크 */}
        <div className="rounded-lg border-l-4 border-l-blue-500 bg-white p-2 shadow-sm">
          <div className="flex items-center gap-1">
            <span className="text-[8px] text-gray-400">15:00</span>
            <span className="text-[10px]">🎢</span>
            <span className="text-[10px] font-semibold text-gray-800">빈펄랜드 테마파크</span>
          </div>
          <div className="mt-0.5 flex gap-1">
            <span className="rounded-full bg-pink-50 px-1 py-0.5 text-[7px] text-pink-600">아이들 취향</span>
          </div>
        </div>

        {/* + 위치 1: 테마파크-야시장 사이 */}
        <PlusButton pos={1} />
        <NewItem pos={1} title="해변 카페 휴식" emoji="☕" color="border-l-emerald-500 bg-emerald-50 ring-emerald-200" time="17:00" match="리조트 근처 · 오션뷰" />

        {/* 야시장 */}
        <div className="rounded-lg border-l-4 border-l-emerald-500 bg-white p-2 shadow-sm">
          <div className="flex items-center gap-1">
            <span className="text-[8px] text-gray-400 transition-all duration-500">{adjusted && insertPos === 1 ? "19:30" : "19:00"}</span>
            <span className="text-[10px]">🌙</span>
            <span className="text-[10px] font-semibold text-gray-800">나트랑 야시장</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function ItineraryMockupScreenV2() {
  const [activeDay, setActiveDay] = useState(0)
  const [showMap, setShowMap] = useState(false)

  const days = [
    { label: "D1", icon: "🏛️", city: "리스본", items: [
      { time: "09:00", emoji: "🏨", name: "리스본 부티크 호텔", color: "border-l-purple-500 bg-purple-50" },
      { time: "12:00", emoji: "🍽️", name: "타임아웃 마켓", color: "border-l-orange-500 bg-orange-50" },
      { time: "15:00", emoji: "🎯", name: "벨렘 타워 관광", color: "border-l-blue-500 bg-blue-50" },
      { time: "18:00", emoji: "🎵", name: "파두 라이브 디너", color: "border-l-emerald-500 bg-emerald-50" },
    ], mapPath: "M40 70 Q100 30 150 55 Q200 75 250 25", markers: [{x:14,y:60,c:"bg-purple-500"},{x:36,y:25,c:"bg-orange-500"},{x:55,y:48,c:"bg-blue-500"},{x:88,y:18,c:"bg-emerald-500"}] },
    { label: "D2", icon: "🍷", city: "리스본", items: [
      { time: "10:00", emoji: "🎯", name: "알파마 지구 투어", color: "border-l-blue-500 bg-blue-50" },
      { time: "13:00", emoji: "🍽️", name: "바칼라우 맛집", color: "border-l-orange-500 bg-orange-50" },
      { time: "16:00", emoji: "🛍️", name: "시아두 쇼핑거리", color: "border-l-pink-500 bg-pink-50" },
      { time: "19:00", emoji: "🌙", name: "리베이라 야경", color: "border-l-emerald-500 bg-emerald-50" },
    ], mapPath: "M30 40 Q80 70 150 35 Q210 60 260 40", markers: [{x:10,y:35,c:"bg-blue-500"},{x:30,y:62,c:"bg-orange-500"},{x:55,y:28,c:"bg-pink-500"},{x:90,y:35,c:"bg-emerald-500"}] },
    { label: "D3", icon: "🚄", city: "신트라", items: [
      { time: "09:00", emoji: "🚄", name: "리스본→신트라 기차", color: "border-l-slate-500 bg-slate-50" },
      { time: "11:00", emoji: "🏰", name: "레갈레이라 궁전", color: "border-l-blue-500 bg-blue-50" },
      { time: "14:00", emoji: "🍽️", name: "신트라 전통 식당", color: "border-l-orange-500 bg-orange-50" },
      { time: "16:00", emoji: "🏛️", name: "페나 궁전", color: "border-l-purple-500 bg-purple-50" },
    ], mapPath: "M25 60 Q70 20 140 45 Q200 30 260 55", markers: [{x:8,y:55,c:"bg-slate-500"},{x:28,y:15,c:"bg-blue-500"},{x:52,y:38,c:"bg-orange-500"},{x:90,y:50,c:"bg-purple-500"}] },
    { label: "D4", icon: "🏖️", city: "카스카이스", items: [
      { time: "10:00", emoji: "🏖️", name: "카스카이스 해변", color: "border-l-blue-500 bg-blue-50" },
      { time: "13:00", emoji: "🍽️", name: "해산물 레스토랑", color: "border-l-orange-500 bg-orange-50" },
      { time: "15:00", emoji: "🎯", name: "보카 도 인페르노", color: "border-l-blue-500 bg-blue-50" },
    ], mapPath: "M50 50 Q130 25 200 60", markers: [{x:18,y:45,c:"bg-blue-500"},{x:48,y:20,c:"bg-orange-500"},{x:72,y:55,c:"bg-blue-500"}] },
  ]

  // 자동 Day 전환 + 타임라인/지도 토글
  useEffect(() => {
    const sequence = [
      { day: 0, map: false, delay: 2500 },
      { day: 0, map: true, delay: 2000 },
      { day: 1, map: false, delay: 2500 },
      { day: 1, map: true, delay: 2000 },
      { day: 2, map: false, delay: 2500 },
      { day: 2, map: true, delay: 2000 },
      { day: 3, map: false, delay: 2500 },
      { day: 3, map: true, delay: 2000 },
    ]
    let step = 0
    let timeout: ReturnType<typeof setTimeout>
    const run = () => {
      const s = sequence[step]
      setActiveDay(s.day)
      setShowMap(s.map)
      step = (step + 1) % sequence.length
      timeout = setTimeout(run, s.delay)
    }
    run()
    return () => clearTimeout(timeout)
  }, [])

  const day = days[activeDay]

  return (
    <div className="p-3 bg-[#FFF8F0] h-full overflow-hidden">
      <p className="text-[12px] font-bold text-gray-800">📋 일정표</p>

      {/* Day 그리드 (현재 디자인 반영) */}
      <div className="mt-1.5 grid grid-cols-4 gap-1">
        {days.map((d, i) => (
          <div key={i} className={`rounded-lg py-1 text-center transition-all duration-500 ${
            activeDay === i
              ? "bg-[#FF6B4A] text-white font-bold shadow-sm"
              : "border border-gray-200 bg-white"
          }`}>
            <span className="text-sm leading-none">{d.icon}</span>
            <p className="text-[9px] font-bold">{d.label}</p>
            <p className={`text-[7px] ${activeDay === i ? "text-white/70" : "text-gray-400"}`}>{d.city}</p>
          </div>
        ))}
      </div>

      {/* 타임라인/지도 토글 */}
      <div className="mt-1.5 flex gap-1">
        <div className={`rounded-full px-2.5 py-0.5 text-[8px] font-medium transition-all duration-300 ${!showMap ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-500"}`}>타임라인</div>
        <div className={`rounded-full px-2.5 py-0.5 text-[8px] font-medium transition-all duration-300 ${showMap ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-500"}`}>지도</div>
      </div>

      {/* 콘텐츠: 타임라인 또는 지도 */}
      <div className="mt-1.5 relative">
        {/* 타임라인 */}
        <div className={`transition-all duration-500 ${!showMap ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4 absolute inset-0 pointer-events-none"}`}>
          <div className="space-y-1">
            {day.items.map((item, j) => (
              <div key={`${activeDay}-${j}`} className={`rounded-lg border-l-4 px-2 py-1.5 transition-all duration-300 ${item.color}`} style={{ animationDelay: `${j * 80}ms` }}>
                <div className="flex items-center gap-1">
                  <span className="text-[8px] text-gray-400">{item.time}</span>
                  <span className="text-[9px]">{item.emoji}</span>
                  <span className="text-[9px] font-medium text-gray-800">{item.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 지도 */}
        <div className={`transition-all duration-500 ${showMap ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4 absolute inset-0 pointer-events-none"}`}>
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <div className="relative h-[180px] bg-gradient-to-br from-[#E8F5E9] via-[#E3F2FD] to-[#F1F8E9]">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 280 120" fill="none">
                <path d={day.mapPath} stroke="#FF6B4A" strokeWidth="2.5" strokeDasharray="5 4" fill="none" opacity="0.7" />
              </svg>
              {day.markers.map((m, k) => (
                <div key={`${activeDay}-m-${k}`} className="absolute flex flex-col items-center transition-all duration-500" style={{ left: `${m.x}%`, top: `${m.y}%` }}>
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full ${m.c} text-[9px] font-bold text-white shadow-lg ring-2 ring-white`}>{k + 1}</div>
                </div>
              ))}
              <div className="absolute bottom-1.5 right-2 rounded bg-white/90 px-1.5 py-0.5 text-[7px] text-gray-600 shadow-sm font-medium">
                📍 {day.city}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ChatMockupScreenV2() {
  const [msgPhase, setMsgPhase] = useState(0)

  useEffect(() => {
    const delays = [2000, 1500, 2500, 2000, 1500, 2500]
    let step = 0
    let timeout: ReturnType<typeof setTimeout>
    const run = () => {
      setMsgPhase(step % 6)
      step++
      timeout = setTimeout(run, delays[step % delays.length])
    }
    timeout = setTimeout(run, 1500)
    return () => clearTimeout(timeout)
  }, [])

  const conversations = [
    { q: "사그라다 파밀리아 예약 어떻게 해?", a: "📍 **예약 가이드**\n• ⏰ 추천: 오전 9시\n• 💰 입장료: €26\n• 💡 공식 사이트 예약 필수!" },
    { q: "Day 3 비가 오면 대안 일정 추천해줘", a: "🌧️ **Day 3 우천 대안**\n• 🏛️ 국립미술관 (실내)\n• 🍷 와이너리 투어\n• 🛍️ 시내 쇼핑몰" },
    { q: "예산 절약할 수 있는 방법은?", a: "💰 **절약 팁**\n• 🎫 시티패스 구매 (30% 절약)\n• 🍽️ 점심 메뉴 델 디아 활용\n• 🚇 10회권 교통카드" },
  ]
  const convIdx = Math.floor(msgPhase / 2) % conversations.length
  const showAnswer = msgPhase % 2 === 1
  const conv = conversations[convIdx]

  return (
    <div className="p-3 bg-[#FFF8F0] h-full overflow-hidden">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FF6B4A]/10 text-[10px]">🤖</div>
        <p className="text-[12px] font-bold text-gray-800">AI 여행 어시스턴트</p>
      </div>
      <div className="space-y-2">
        {/* 사용자 질문 */}
        <div key={`q-${convIdx}`} className="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-[#FF6B4A] px-3 py-2 text-[10px] text-white shadow-sm animate-in fade-in slide-in-from-right-2 duration-500">
          {conv.q}
        </div>

        {/* AI 응답 */}
        <div className={`flex items-end gap-1.5 transition-all duration-700 ${showAnswer ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[8px]">🤖</div>
          <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border px-3 py-2 text-[10px] text-gray-700 shadow-sm">
            {conv.a.split('\n').map((line, i) => (
              <p key={i} className={i === 0 ? "font-semibold text-gray-800" : "mt-0.5"}>{line}</p>
            ))}
          </div>
        </div>

        {/* 타이핑 인디케이터 */}
        <div className={`flex items-end gap-1.5 transition-all duration-300 ${!showAnswer ? "opacity-100" : "opacity-0 h-0"}`}>
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[8px]">🤖</div>
          <div className="rounded-2xl rounded-bl-md bg-gray-100 px-4 py-2.5">
            <div className="flex gap-1">
              <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" style={{animationDelay:"0ms"}} />
              <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" style={{animationDelay:"150ms"}} />
              <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" style={{animationDelay:"300ms"}} />
            </div>
          </div>
        </div>

        {/* 추천 칩 */}
        <div className="flex flex-wrap gap-1 pt-1">
          <span className="rounded-full border border-[#FF6B4A]/30 bg-white px-2 py-0.5 text-[8px] text-[#FF6B4A]">현지 교통 이용 팁</span>
          <span className="rounded-full border border-[#FF6B4A]/30 bg-white px-2 py-0.5 text-[8px] text-[#FF6B4A]">맛집 추천해줘</span>
          <span className="rounded-full border border-[#FF6B4A]/30 bg-white px-2 py-0.5 text-[8px] text-[#FF6B4A]">날씨 어때?</span>
        </div>
      </div>
    </div>
  )
}

function LanguageMockupScreen() {
  const [activeCat, setActiveCat] = useState(0)

  const categories = [
    { label: "인사", color: "bg-blue-500", phrases: [
      { ko: "안녕하세요", local: "Hola", pron: "올라" },
      { ko: "감사합니다", local: "Gracias", pron: "그라시아스" },
      { ko: "실례합니다", local: "Perdón", pron: "뻬르돈" },
      { ko: "안녕히 가세요", local: "Adiós", pron: "아디오스" },
    ]},
    { label: "식당", color: "bg-orange-500", phrases: [
      { ko: "메뉴 주세요", local: "La carta, por favor", pron: "라 카르타, 포르 파보르" },
      { ko: "계산서 주세요", local: "La cuenta", pron: "라 꾸엔따" },
      { ko: "추천 메뉴는?", local: "¿Qué recomienda?", pron: "께 레꼬미엔다?" },
      { ko: "맛있어요!", local: "¡Está delicioso!", pron: "에스따 델리시오소!" },
    ]},
    { label: "교통", color: "bg-slate-500", phrases: [
      { ko: "여기까지 가주세요", local: "Lléveme aquí", pron: "예베메 아끼" },
      { ko: "얼마예요?", local: "¿Cuánto cuesta?", pron: "꾸안또 꾸에스따?" },
      { ko: "지하철역 어디예요?", local: "¿Dónde está el metro?", pron: "돈데 에스따 엘 메트로?" },
    ]},
    { label: "긴급", color: "bg-red-500", phrases: [
      { ko: "도와주세요!", local: "¡Ayuda!", pron: "아유다!" },
      { ko: "경찰을 불러주세요", local: "Llame a la policía", pron: "야메 아 라 뽈리시아" },
      { ko: "병원 어디예요?", local: "¿Dónde está el hospital?", pron: "돈데 에스따 엘 오스삐딸?" },
    ]},
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCat(prev => (prev + 1) % categories.length)
    }, 3000)
    return () => clearInterval(timer)
  }, [])

  const cat = categories[activeCat]

  return (
    <div className="p-3 bg-[#FFF8F0] h-full overflow-hidden">
      <p className="text-[12px] font-bold text-gray-800">🗣️ 현지어 가이드</p>
      <p className="text-[8px] text-gray-400 mt-0.5">스페인어 · 필수 회화 20개+</p>

      {/* 카테고리 탭 */}
      <div className="mt-2 flex gap-1">
        {categories.map((c, i) => (
          <div key={i} className={`rounded-full px-2 py-0.5 text-[8px] font-medium transition-all duration-300 ${
            activeCat === i ? `${c.color} text-white` : "bg-gray-100 text-gray-500"
          }`}>{c.label}</div>
        ))}
      </div>

      {/* 구문 리스트 */}
      <div className="mt-2.5 space-y-2">
        {cat.phrases.map((p, j) => (
          <div key={`${activeCat}-${j}`} className="rounded-xl border-l-4 bg-white p-2.5 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-300" style={{
            borderColor: activeCat === 0 ? "#3B82F6" : activeCat === 1 ? "#F97316" : activeCat === 2 ? "#64748B" : "#EF4444",
            animationDelay: `${j * 100}ms`
          }}>
            <p className="text-[8px] text-gray-400">{p.ko}</p>
            <p className="text-[14px] font-bold text-gray-800 mt-0.5">{p.local}</p>
            <p className="text-[10px] text-[#FF6B4A] mt-0.5">{p.pron}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function SettlementMockupScreenV2() {
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab(prev => (prev + 1) % 3)
    }, 3500)
    return () => clearInterval(timer)
  }, [])

  const tabs = ["경비 목록", "정산 결과", "그룹 설정"]

  return (
    <div className="p-3 bg-[#FFF8F0] h-full overflow-hidden">
      <p className="text-[12px] font-bold text-gray-800">💰 경비 정산</p>
      <div className="mt-1.5 flex gap-1">
        {tabs.map((t, i) => (
          <div key={i} className={`rounded-full px-2 py-0.5 text-[8px] font-medium transition-all duration-300 ${
            activeTab === i ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-500"
          }`}>{t}</div>
        ))}
      </div>

      <div className="mt-2 relative">
        {/* 탭 0: 경비 목록 */}
        <div className={`transition-all duration-500 ${activeTab === 0 ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4 absolute inset-0 pointer-events-none"}`}>
          <div className="flex items-center gap-1 mb-1.5">
            <span className="rounded-full bg-[#FF6B4A]/10 px-1.5 py-0.5 text-[7px] font-medium text-[#FF6B4A]">가족균등</span>
            <span className="text-[8px] text-gray-400">총 8건</span>
          </div>
          <div className="space-y-1.5">
            <div className="rounded-lg border bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">🍽️</span>
                  <div>
                    <p className="text-[10px] font-semibold text-gray-800">점심 식사</p>
                    <span className="rounded bg-indigo-50 px-1 py-0.5 text-[7px] text-indigo-600">김민수 결제</span>
                  </div>
                </div>
                <span className="text-[11px] font-bold text-gray-800">€45</span>
              </div>
            </div>
            <div className="rounded-lg border bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">🎫</span>
                  <div>
                    <p className="text-[10px] font-semibold text-gray-800">사그라다 파밀리아</p>
                    <span className="rounded bg-purple-50 px-1 py-0.5 text-[7px] text-purple-600">최현우 결제</span>
                  </div>
                </div>
                <span className="text-[11px] font-bold text-gray-800">€52</span>
              </div>
            </div>
            <div className="rounded-lg border bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">☕</span>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] font-semibold text-gray-800">카페</p>
                      <span className="rounded bg-amber-50 px-1 py-0.5 text-[6px] text-amber-600">🎁 찬조</span>
                    </div>
                    <span className="rounded bg-green-50 px-1 py-0.5 text-[7px] text-green-600">한서윤 결제</span>
                  </div>
                </div>
                <span className="text-[11px] font-bold text-gray-800">€18</span>
              </div>
            </div>
          </div>
          <div className="mt-2 rounded-xl bg-white border p-2 text-center shadow-sm">
            <p className="text-[7px] text-gray-400">총 경비</p>
            <p className="text-[20px] font-extrabold text-gray-800">€1,247</p>
          </div>
        </div>

        {/* 탭 1: 정산 결과 */}
        <div className={`transition-all duration-500 ${activeTab === 1 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4 absolute inset-0 pointer-events-none"}`}>
          <p className="text-[9px] font-semibold text-gray-600 mb-1.5">그룹별 잔액</p>
          <div className="space-y-1.5">
            <div className="rounded-lg border bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between"><span className="text-[9px] font-medium">A가족 (김민수)</span><span className="text-[10px] font-bold text-green-600">+€127</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-gray-100"><div className="h-1.5 w-[65%] rounded-full bg-green-400 transition-all duration-1000" /></div>
            </div>
            <div className="rounded-lg border bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between"><span className="text-[9px] font-medium">B가족 (최현우)</span><span className="text-[10px] font-bold text-red-500">-€89</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-gray-100"><div className="h-1.5 w-[45%] rounded-full bg-red-400 transition-all duration-1000" /></div>
            </div>
            <div className="rounded-lg border bg-white p-2 shadow-sm">
              <div className="flex items-center justify-between"><span className="text-[9px] font-medium">할머니 (한서윤)</span><span className="text-[10px] font-bold text-red-500">-€38</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-gray-100"><div className="h-1.5 w-[20%] rounded-full bg-red-400 transition-all duration-1000" /></div>
            </div>
          </div>
          <p className="mt-2 text-[9px] font-semibold text-gray-600">정산 안내</p>
          <div className="mt-1 space-y-1">
            <div className="rounded-xl border border-[#FF6B4A]/20 bg-[#FF6B4A]/5 p-2 text-center shadow-sm">
              <p className="text-[8px] text-gray-500">B가족 → A가족</p>
              <p className="text-[16px] font-extrabold text-[#FF6B4A]">€89</p>
            </div>
            <div className="rounded-xl border border-[#FF6B4A]/20 bg-[#FF6B4A]/5 p-2 text-center shadow-sm">
              <p className="text-[8px] text-gray-500">할머니 → A가족</p>
              <p className="text-[16px] font-extrabold text-[#FF6B4A]">€38</p>
            </div>
          </div>
        </div>

        {/* 탭 2: 그룹 설정 */}
        <div className={`transition-all duration-500 ${activeTab === 2 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4 absolute inset-0 pointer-events-none"}`}>
          <div className="space-y-2">
            <div className="rounded-xl border bg-white p-2.5 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-gray-800">👨‍👩‍👧‍👦 A가족</p>
                <span className="rounded bg-[#FF6B4A]/10 px-1.5 py-0.5 text-[7px] text-[#FF6B4A]">대표: 김민수</span>
              </div>
              <div className="mt-1.5 flex gap-1 flex-wrap">
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px]">👤 김민수 <span className="text-gray-400">성인</span></span>
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px]">👤 이지은 <span className="text-gray-400">성인</span></span>
                <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[8px]">👶 김서준 <span className="text-gray-400">아동</span></span>
                <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[8px]">👶 김유주 <span className="text-gray-400">아동</span></span>
              </div>
            </div>
            <div className="rounded-xl border bg-white p-2.5 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-gray-800">👨‍👩‍👧‍👦 B가족</p>
                <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[7px] text-purple-600">대표: 최현우</span>
              </div>
              <div className="mt-1.5 flex gap-1 flex-wrap">
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px]">👤 최현우 <span className="text-gray-400">성인</span></span>
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px]">👤 박지영 <span className="text-gray-400">성인</span></span>
                <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[8px]">👶 최하은 <span className="text-gray-400">아동</span></span>
              </div>
            </div>
            <div className="rounded-xl border bg-white p-2.5 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-gray-800">👵 할머니</p>
                <span className="rounded bg-green-50 px-1.5 py-0.5 text-[7px] text-green-600">대표: 한서윤</span>
              </div>
              <div className="mt-1.5">
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px]">👤 한서윤 <span className="text-gray-400">성인</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function HeroPhoneMockup() {
  return (
    <PhoneMockup>
      <div className="p-4 bg-[#FFF8F0]">
        {/* 헤더 */}
        <div className="flex items-center justify-between">
          <p className="text-[13px] font-bold text-gray-800">내 여행</p>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FF6B4A]/10">
            <span className="text-[10px]">👤</span>
          </div>
        </div>
        <div className="mt-3 space-y-2.5">
          <div className="rounded-2xl border bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold">나트랑 가족여행</p>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[8px] font-medium text-amber-700">설문진행</span>
            </div>
            <p className="mt-1 text-[9px] text-gray-400">나트랑 &middot; 5/14~5/17 &middot; 10명</p>
            <div className="mt-1.5 flex gap-1">
              <div className="h-1 flex-1 rounded-full bg-[#FF6B4A]/20"><div className="h-1 w-[40%] rounded-full bg-[#FF6B4A]" /></div>
              <span className="text-[7px] text-gray-400">4/10 응답</span>
            </div>
          </div>
          <div className="rounded-2xl border bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold">스페인 친구여행</p>
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[8px] font-medium text-green-700">확정</span>
            </div>
            <p className="mt-1 text-[9px] text-gray-400">바르셀로나 &middot; 6/1~6/8 &middot; 4명</p>
            <div className="mt-1.5 flex items-center gap-1">
              <div className="flex -space-x-1">
                <div className="h-4 w-4 rounded-full bg-blue-200 ring-1 ring-white" />
                <div className="h-4 w-4 rounded-full bg-pink-200 ring-1 ring-white" />
                <div className="h-4 w-4 rounded-full bg-green-200 ring-1 ring-white" />
                <div className="h-4 w-4 rounded-full bg-amber-200 ring-1 ring-white" />
              </div>
              <span className="text-[7px] text-gray-400">전원 완료</span>
            </div>
          </div>
          <div className="rounded-2xl border bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold">포르투갈 가족여행</p>
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[8px] font-medium text-blue-700">일정완성</span>
            </div>
            <p className="mt-1 text-[9px] text-gray-400">리스본 &middot; 7/20~7/27 &middot; 6명</p>
            <div className="mt-1.5 flex gap-1">
              <span className="rounded bg-purple-50 px-1 py-0.5 text-[7px] text-purple-600">D-121</span>
              <span className="rounded bg-gray-50 px-1 py-0.5 text-[7px] text-gray-500">4박 7일</span>
            </div>
          </div>
          <div className="rounded-2xl border-2 border-dashed border-[#FF6B4A]/30 p-3 text-center">
            <p className="text-[11px] font-semibold text-[#FF6B4A]">+ 새 여행 만들기</p>
          </div>
        </div>
      </div>
    </PhoneMockup>
  )
}

/* --- 헤더 --- */
function LandingHeader() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-lg shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <a
          href="#"
          className="font-outfit text-[22px] font-bold text-coral"
          onClick={(e) => {
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: "smooth" })
          }}
        >
          TasteGo
        </a>

        {/* 데스크톱 네비게이션 */}
        <nav className="hidden items-center gap-8 md:flex">
          <a
            href="#features"
            className="font-inter text-sm font-medium text-text-secondary hover:text-coral transition-colors"
          >
            기능
          </a>
          <a
            href="#use-cases"
            className="font-inter text-sm font-medium text-text-secondary hover:text-coral transition-colors"
          >
            후기
          </a>
          <Link
            href="/signup"
            className="rounded-full bg-coral px-5 py-2 font-inter text-sm font-semibold text-white transition-colors hover:bg-coral-dark"
          >
            시작하기
          </Link>
        </nav>

        {/* 모바일 햄버거 */}
        <button
          className="flex flex-col gap-1.5 md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="메뉴 열기"
        >
          <span
            className={`block h-0.5 w-6 bg-text-primary transition-transform ${
              mobileOpen ? "translate-y-2 rotate-45" : ""
            }`}
          />
          <span
            className={`block h-0.5 w-6 bg-text-primary transition-opacity ${
              mobileOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block h-0.5 w-6 bg-text-primary transition-transform ${
              mobileOpen ? "-translate-y-2 -rotate-45" : ""
            }`}
          />
        </button>
      </div>

      {/* 모바일 메뉴 */}
      {mobileOpen && (
        <div className="border-t border-gray-100 bg-white/95 backdrop-blur-lg md:hidden">
          <nav className="flex flex-col items-center gap-4 px-5 py-6">
            <a
              href="#features"
              className="font-inter text-base font-medium text-text-secondary"
              onClick={() => setMobileOpen(false)}
            >
              기능
            </a>
            <a
              href="#use-cases"
              className="font-inter text-base font-medium text-text-secondary"
              onClick={() => setMobileOpen(false)}
            >
              후기
            </a>
            <Link
              href="/signup"
              className="mt-2 w-full rounded-full bg-coral py-3 text-center font-inter text-sm font-semibold text-white"
              onClick={() => setMobileOpen(false)}
            >
              시작하기
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}

/* --- 히어로 --- */
function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-cream pt-28 pb-16 md:pt-36 md:pb-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col items-center gap-12 md:flex-row md:gap-16">
          {/* 텍스트 */}
          <div className="flex-1 text-center md:text-left">
            <FadeIn>
              <span className="inline-block rounded-full bg-coral/10 px-4 py-1.5 font-inter text-sm font-medium text-coral">
                AI가 만드는 맞춤 여행
              </span>
            </FadeIn>
            <FadeIn delay={100}>
              <h1 className="mt-6 font-outfit text-[32px] font-extrabold leading-tight text-text-primary md:text-[52px]">
                우리 가족, 친구
                <br />
                모두가 좋아하는
                <br />
                여행을 만들어요
              </h1>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="mt-5 font-inter text-[15px] leading-relaxed text-text-secondary md:text-[17px]">
                10명이 함께 가도 한 명도 빠짐없이 만족!
                <br />
                각자의 취향을 AI가 분석해서 딱 맞는 코스를 추천해드려요.
              </p>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row md:items-start">
                <Link
                  href="/signup"
                  className="w-full rounded-full bg-coral px-7 py-3.5 text-center font-inter text-[15px] font-semibold text-white shadow-lg shadow-coral/25 transition-all hover:bg-coral-dark hover:shadow-xl hover:shadow-coral/30 sm:w-auto"
                >
                  무료로 시작하기
                </Link>
                <a
                  href="#flow"
                  className="w-full rounded-full border-2 border-coral px-7 py-3.5 text-center font-inter text-[15px] font-semibold text-coral transition-colors hover:bg-coral/5 sm:w-auto"
                >
                  어떻게 작동하나요?
                </a>
              </div>
            </FadeIn>
          </div>

          {/* 앱 프리뷰 */}
          <FadeIn delay={200} className="flex-1">
            <HeroPhoneMockup />
          </FadeIn>
        </div>
      </div>
    </section>
  )
}

/* --- 플로우 (3단계) --- */
function FlowSection() {
  const steps = [
    {
      num: "\u2460",
      emoji: "📮",
      title: "링크 하나로 설문",
      desc: "구성원에게 링크 하나만 공유하면\nAI가 나이와 성별에 맞는 맞춤 설문을\n자동으로 생성해요",
    },
    {
      num: "\u2461",
      emoji: "🤖",
      title: "AI가 취향 분석",
      desc: "모든 구성원의 답변을 분석하고\n공통점은 강화, 차이점은 절충하여\n최적의 코스를 설계해요",
    },
    {
      num: "\u2462",
      emoji: "🛫",
      title: "완벽한 여행 출발",
      desc: "일정표, 지도, 현지어 가이드,\nAI 챗봇, 경비 정산까지\n올인원으로 준비 끝!",
    },
  ]

  return (
    <section id="flow" className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <FadeIn>
          <div className="text-center">
            <h2 className="font-outfit text-[28px] font-bold text-text-primary md:text-[36px]">
              이렇게 간단해요
            </h2>
            <p className="mt-3 font-inter text-[15px] text-text-secondary md:text-[17px]">
              복잡한 여행 계획, AI가 대신 해드려요
            </p>
          </div>
        </FadeIn>

        <div className="mt-14 grid gap-6 md:grid-cols-3 md:gap-4">
          {steps.map((step, i) => (
            <FadeIn key={step.title} delay={i * 150}>
              <div className="relative flex flex-col items-center rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-100 transition-shadow hover:shadow-md">
                {/* 스텝 번호 배지 */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-[#FF6B4A] text-[14px] font-bold text-white shadow-md">
                  {step.num}
                </div>
                <span className="mt-2 text-5xl">{step.emoji}</span>
                <h3 className="mt-5 font-outfit text-lg font-bold text-text-primary">
                  {step.title}
                </h3>
                <p className="mt-2 whitespace-pre-line font-inter text-sm leading-relaxed text-text-secondary">
                  {step.desc}
                </p>
                {/* 데스크톱 연결 화살표 */}
                {i < steps.length - 1 && (
                  <div className="absolute -right-5 top-1/2 hidden -translate-y-1/2 text-[24px] text-[#FF6B4A]/40 md:block">
                    →
                  </div>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  )
}

/* --- 구현된 기능 (지그재그) --- */
function StickyFeatureSection() {
  const [activeIndex, setActiveIndex] = useState(0)
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observers = sectionRefs.current.map((ref, index) => {
      if (!ref) return null
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveIndex(index)
        },
        { threshold: 0.3, rootMargin: "-35% 0px -35% 0px" }
      )
      observer.observe(ref)
      return observer
    })
    return () => observers.forEach(obs => obs?.disconnect())
  }, [])

  const features = [
    {
      emoji: "🎯",
      title: "AI 맞춤 설문",
      description: "5세 아이와 60대 어르신에게 같은 질문을 하지 않습니다.",
      bullets: [
        "나이/성별에 따라 AI가 개인화된 설문 자동 생성",
        "하나의 링크로 모든 구성원이 참여",
        "객관식 중심 + 기타 직접 입력",
        "목적지/숙소에 맞는 현지 맞춤 질문"
      ]
    },
    {
      emoji: "🗺️",
      title: "AI 여행 코스 설계",
      description: "설문 결과를 분석하여 모두의 취향이 반영된 일자별 코스를 설계합니다.",
      bullets: [
        "구성원 취향 매칭 태그 (누구의 취향인지 표시)",
        "일정 사이 + 버튼으로 AI 자동 추천 추가",
        "카테고리별 색상 코딩 (숙소/맛집/관광/이동)",
        "다국가/다도시 여행도 지원"
      ]
    },
    {
      emoji: "📍",
      title: "일정표 + 지도 뷰",
      description: "확정된 일정을 타임라인과 지도에서 한눈에 확인하세요.",
      bullets: [
        "Day별 테마 자동 감지 (🏛️탐방 / 🍷미식 / 🚄이동)",
        "지도에 번호 마커 + 동선 경로 표시",
        "드래그앤드롭으로 순서 변경",
        "HTML/PDF/이미지 내보내기 + 공유 링크"
      ]
    },
    {
      emoji: "💬",
      title: "AI 여행 챗봇",
      description: "여행에 대해 뭐든 물어보세요. 일정 기반으로 정확한 답변을 드려요.",
      bullets: [
        "확정 일정 전체를 알고 있는 AI 어시스턴트",
        "실시간 스트리밍 답변 (마크다운 지원)",
        "예약 방법, 현지 팁, 대안 일정 추천",
        "추천 질문 칩으로 쉽게 시작"
      ]
    },
    {
      emoji: "🗣️",
      title: "현지어 가이드",
      description: "발음까지 알려주는 필수 현지어 회화. 카테고리별로 빠르게 찾아보세요.",
      bullets: [
        "20개+ 필수 회화 (인사/식당/교통/쇼핑/긴급)",
        "현지어 + 한국어 발음 가이드",
        "카테고리별 필터로 빠르게 검색",
        "목적지에 맞는 언어 자동 생성"
      ]
    },
    {
      emoji: "💰",
      title: "경비 정산",
      description: "가족별, 인원별 유연한 정산. 찬조금까지 깔끔하게.",
      bullets: [
        "가족 그룹 설정 (대표자 지정)",
        "4가지 정산 방식 (가족균등/어른기준/인원기준/직접지정)",
        "찬조/후원금 + 대납 지원",
        "최소 이체 횟수 자동 계산"
      ]
    }
  ]

  const screens = [
    <SurveyAutoSlide key="survey" />,
    <CourseMockupScreenV2 key="course" />,
    <ItineraryMockupScreenV2 key="itinerary" />,
    <ChatMockupScreenV2 key="chat" />,
    <LanguageMockupScreen key="language" />,
    <SettlementMockupScreenV2 key="settlement" />,
  ]

  return (
    <section id="features" className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-[#FF6B4A] font-semibold text-sm tracking-wide">FEATURES</span>
            <h2 className="font-outfit text-[28px] font-extrabold text-[#2D2D2D] mt-2 md:text-[36px]">
              지금 바로 쓸 수 있어요
            </h2>
          </div>
        </FadeIn>

        {/* 데스크톱: 스티키 스크롤 */}
        <div className="hidden md:flex gap-16">
          {/* 왼쪽: 스크롤 텍스트 패널 */}
          <div className="w-1/2">
            {features.map((f, i) => (
              <div
                key={i}
                ref={el => { sectionRefs.current[i] = el }}
                className="min-h-screen flex items-center py-20"
              >
                <div className={`transition-all duration-500 ${activeIndex === i ? "opacity-100 translate-y-0" : "opacity-30 translate-y-2"}`}>
                  <span className="text-4xl">{f.emoji}</span>
                  <h3 className="mt-4 text-2xl font-bold text-[#2D2D2D] font-outfit">{f.title}</h3>
                  <p className="mt-3 text-[#7C7C7C] leading-relaxed">{f.description}</p>
                  <ul className="mt-4 space-y-2">
                    {f.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#5C5C5C]">
                        <span className="text-[#FF6B4A] mt-0.5 shrink-0">&#10003;</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* 오른쪽: 스티키 폰 목업 */}
          <div className="w-1/2">
            <div className="sticky top-20 flex items-center justify-center h-screen">
              <PhoneMockup>
                <div className="relative h-full">
                  {screens.map((screen, i) => (
                    <div
                      key={i}
                      className={`absolute inset-0 transition-all duration-700 ${
                        activeIndex === i
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4 pointer-events-none"
                      }`}
                    >
                      {screen}
                    </div>
                  ))}
                </div>
              </PhoneMockup>
            </div>
          </div>
        </div>

        {/* 모바일: 스택형 카드 (스티키 없음) */}
        <div className="md:hidden space-y-12">
          {features.map((f, i) => (
            <FadeIn key={i}>
              <div className="space-y-4">
                <span className="text-4xl">{f.emoji}</span>
                <h3 className="text-xl font-bold text-[#2D2D2D] font-outfit">{f.title}</h3>
                <p className="text-[#7C7C7C] text-sm leading-relaxed">{f.description}</p>
                <ul className="space-y-1.5">
                  {f.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#5C5C5C]">
                      <span className="text-[#FF6B4A] mt-0.5 shrink-0">&#10003;</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <PhoneMockup>
                  {screens[i]}
                </PhoneMockup>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  )
}

/* --- 커밍 순 --- */
function ComingSoonSection() {
  const items = [
    { emoji: "🌤️", title: "실시간 날씨", desc: "여행 기간 날씨 + 비 오는 날 대안 일정" },
    { emoji: "✈️", title: "항공편 검색", desc: "실시간 항공편 비교" },
    { emoji: "⭐", title: "여행 후기", desc: "구성원별 리뷰 + 만족도" },
    { emoji: "🌐", title: "커뮤니티", desc: "다른 사람들의 여행 코스" },
    { emoji: "📱", title: "카카오톡 알림", desc: "설문/리뷰 링크 카톡 발송" },
    { emoji: "🔔", title: "D-day 알림", desc: "여행 전 준비 리마인더" },
  ]

  return (
    <section className="bg-cream py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <FadeIn>
          <div className="text-center">
            <h2 className="font-outfit text-[28px] font-bold text-text-primary md:text-[36px]">
              더 좋아질 거예요
            </h2>
          </div>
        </FadeIn>

        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          {items.map((item, i) => (
            <FadeIn key={item.title} delay={i * 80}>
              <div className="relative rounded-2xl bg-white/60 p-5 text-center ring-1 ring-gray-100/50 md:p-7">
                <span className="absolute right-3 top-3 rounded-full bg-gray-100 px-2.5 py-0.5 font-inter text-[10px] font-medium text-text-muted">
                  Coming Soon
                </span>
                <span className="text-3xl md:text-4xl">{item.emoji}</span>
                <h3 className="mt-3 font-outfit text-sm font-bold text-text-primary md:text-base">
                  {item.title}
                </h3>
                <p className="mt-1.5 font-inter text-xs leading-relaxed text-text-secondary md:text-sm">
                  {item.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  )
}

/* --- 유스케이스 --- */
function UseCasesSection() {
  const cases = [
    {
      emoji: "👨‍👩‍👧‍👦",
      title: "가족 여행",
      desc: "아이부터 어르신까지 모두 만족하는 가족 맞춤 여행",
      bg: "#E8F4FD",
    },
    {
      emoji: "👫",
      title: "커플 여행",
      desc: "서로 다른 취향도 AI가 완벽하게 절충",
      bg: "#E8FAF0",
    },
    {
      emoji: "👥",
      title: "친구 여행",
      desc: "10명이 가도 정산까지 깔끔하게",
      bg: "#F0E8FF",
    },
    {
      emoji: "🏢",
      title: "단체/워크숍",
      desc: "대규모 그룹도 한 번에 설계",
      bg: "#FFF0E8",
    },
  ]

  return (
    <section id="use-cases" className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <FadeIn>
          <div className="text-center">
            <h2 className="font-outfit text-[28px] font-bold text-text-primary md:text-[36px]">
              이런 여행에 TasteGo
            </h2>
          </div>
        </FadeIn>

        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {cases.map((c, i) => (
            <FadeIn key={c.title} delay={i * 100}>
              <div
                className="flex flex-col items-center rounded-2xl p-6 text-center md:p-8"
                style={{ backgroundColor: c.bg }}
              >
                <span className="text-4xl md:text-5xl">{c.emoji}</span>
                <h3 className="mt-4 font-outfit text-base font-bold text-text-primary md:text-lg">
                  {c.title}
                </h3>
                <p className="mt-2 font-inter text-xs leading-relaxed text-text-secondary md:text-sm">
                  {c.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  )
}

/* --- CTA --- */
function CtaSection() {
  return (
    <section className="relative overflow-hidden bg-cream py-20 md:py-28">
      {/* 배경 */}
      <div className="pointer-events-none absolute inset-0 flex select-none items-center justify-center opacity-[0.04] text-[200px] md:text-[300px]">
        ✈️
      </div>

      <div className="relative mx-auto max-w-2xl px-5 text-center">
        <FadeIn>
          <h2 className="font-outfit text-[28px] font-bold text-text-primary md:text-[40px]">
            지금 첫 여행을 만들어보세요
          </h2>
        </FadeIn>
        <FadeIn delay={100}>
          <p className="mt-4 font-inter text-[15px] text-text-secondary md:text-[17px]">
            무료로 시작, 카드 등록 없이
          </p>
        </FadeIn>
        <FadeIn delay={200}>
          <Link
            href="/signup"
            className="mt-8 inline-block rounded-full bg-coral px-10 py-4 font-inter text-[16px] font-semibold text-white shadow-lg shadow-coral/25 transition-all hover:bg-coral-dark hover:shadow-xl hover:shadow-coral/30"
          >
            무료로 시작하기
          </Link>
        </FadeIn>
      </div>
    </section>
  )
}

/* --- 푸터 --- */
function Footer() {
  return (
    <footer className="bg-[#2D2D2D] py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col gap-10 md:flex-row md:justify-between">
          {/* 로고 */}
          <div>
            <span className="font-outfit text-xl font-bold text-white">
              TasteGo
            </span>
            <p className="mt-2 font-inter text-sm text-gray-400">
              취향대로 떠나라.
            </p>
          </div>

          {/* 링크 */}
          <div className="grid grid-cols-3 gap-8 md:gap-16">
            <div>
              <h4 className="font-inter text-sm font-semibold text-gray-300">
                서비스
              </h4>
              <ul className="mt-3 space-y-2">
                <li>
                  <a href="#features" className="font-inter text-sm text-gray-400 hover:text-white transition-colors">
                    주요 기능
                  </a>
                </li>
                <li>
                  <a href="#flow" className="font-inter text-sm text-gray-400 hover:text-white transition-colors">
                    사용 방법
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-inter text-sm font-semibold text-gray-300">
                고객지원
              </h4>
              <ul className="mt-3 space-y-2">
                <li>
                  <span className="font-inter text-sm text-gray-400">FAQ</span>
                </li>
                <li>
                  <span className="font-inter text-sm text-gray-400">문의하기</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-inter text-sm font-semibold text-gray-300">
                법적고지
              </h4>
              <ul className="mt-3 space-y-2">
                <li>
                  <span className="font-inter text-sm text-gray-400">이용약관</span>
                </li>
                <li>
                  <span className="font-inter text-sm text-gray-400">개인정보처리방침</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-gray-700 pt-6">
          <p className="font-inter text-xs text-gray-500">
            &copy; 2026 TasteGo. 취향대로 떠나라.
          </p>
        </div>
      </div>
    </footer>
  )
}

/* --- 메인 --- */
export default function LandingPage() {
  return (
    <div className="min-h-screen scroll-smooth">
      <LandingHeader />
      <main>
        <HeroSection />
        <FlowSection />
        <StickyFeatureSection />
        <ComingSoonSection />
        <UseCasesSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  )
}
